import { Download, Save, AlertTriangle, Check, Droplets, Gauge, Activity, Beaker, Cpu, Zap, RotateCcw, FlaskConical } from "lucide-react";
import { useState } from "react";
import { saveCalculation } from "../../lib/api";
import { normalizeInput } from "../../lib/defaults";
import { generateProDosePDF } from "../../lib/pdf";
import { toast } from "sonner";

export default function StepResults({ state, result, onReset }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await saveCalculation(normalizeInput(state), result);
      setSaved(true);
      toast.success("Calculation saved to history");
    } catch (e) {
      toast.error("Could not save — backend offline?");
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  async function handlePDF() {
    setPdfBusy(true);
    try {
      await generateProDosePDF(normalizeInput(state), result);
      toast.success("PDF downloaded");
    } catch (e) {
      console.error(e);
      toast.error("PDF generation failed");
    } finally {
      setPdfBusy(false);
    }
  }

  if (!result) {
    return <div className="text-center py-20 text-white/60">Calculating…</div>;
  }

  const { dose_ppm, base_demand_ppm, ph_factor, temp_factor, breakdown, stock_gallons_per_day,
          diluted_gallons_per_day, mix_ratio, pump, pump_setting_pct, pulse_mode,
          solution_tank, water_meter, contact_time_minutes, warnings, shock_protocol,
          retention_tank, soda_ash } = result;

  return (
    <section data-testid="step-results" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">
          Dosing<br/>Specification
        </h1>
      </div>

      {/* Action buttons (stacked on mobile) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <button
          type="button"
          data-testid="btn-pdf"
          onClick={handlePDF}
          disabled={pdfBusy}
          className="h-14 px-5 bg-[#F26522] text-black hover:bg-[#FF7A2A] font-heading font-black tracking-wider uppercase text-base flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
        >
          <Download className="w-5 h-5" />
          {pdfBusy ? "Generating…" : "Download PDF"}
        </button>
        <button
          type="button"
          data-testid="btn-save"
          disabled={saving || saved}
          onClick={handleSave}
          className={`h-14 px-5 border-2 font-heading font-black tracking-wider uppercase text-base flex items-center justify-center gap-2 transition-colors ${
            saved ? "bg-[#10B981] border-[#10B981] text-black" :
            "border-white text-white hover:bg-white hover:text-black"
          }`}
        >
          {saved ? <Check className="w-5 h-5" /> : <Save className="w-5 h-5" />}
          {saved ? "Saved" : saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          data-testid="btn-reset"
          onClick={onReset}
          className="h-14 px-5 border-2 border-white/30 bg-transparent text-white hover:border-white font-heading font-black tracking-wider uppercase text-base flex items-center justify-center gap-2 transition-colors"
        >
          <RotateCcw className="w-5 h-5" /> New Calc
        </button>
      </div>

      {/* Project banner */}
      {(state.project_name || state.technician) && (
        <div className="bg-black border-2 border-[#F26522] p-5 grid grid-cols-2 gap-4">
          <Stat label="Project" value={state.project_name || "—"} />
          <Stat label="Technician" value={state.technician || "—"} />
          <Stat label="Stock" value={`${state.stock_chlorine_pct}% NaOCl`} />
          <Stat label="Date" value={new Date().toLocaleDateString()} />
        </div>
      )}

      {/* HERO tiles */}
      <div className="space-y-3">
        <HeroTile orange icon={<Droplets className="w-5 h-5" />} label="Total Chlorine Dose" value={dose_ppm} unit="ppm" testId="result-dose-ppm" />
        <HeroTile black icon={<Cpu className="w-5 h-5" />} label="Recommended Pump" value={pump?.model || "—"} unit={pump?.series || ""} testId="result-pump-model" />
        <HeroTile icon={<Gauge className="w-5 h-5" />} label="Pump Setting" value={`${pump_setting_pct}%`} unit="stroke" testId="result-pump-setting" />
      </div>

      {/* RT40/No-Tank BOOST callout */}
      {retention_tank?.rt40_boost_applied && (
        <div className="bg-[#F26522] text-black p-5 border-l-4 border-black" data-testid="rt40-boost-notice">
          <div className="font-heading text-lg font-black uppercase tracking-tight mb-1 flex items-center gap-2">
            <Zap className="w-5 h-5" /> Dose Boost Active
          </div>
          <p className="text-sm">
            {retention_tank.type === "none"
              ? "No contact tank selected — chlorine dose has been increased so the chemistry completes in the pipe before downstream filtration. "
              : "Contact time is below 10 min — instead of forcing a tank upgrade, the chlorine dose has been increased to compensate. "}
            Effective target residual:
            <strong> {retention_tank.effective_target_residual_ppm} ppm</strong>.
          </p>
        </div>
      )}

      {/* Data tiles */}
      <div className="grid grid-cols-2 gap-3">
        <DataTile label="Base Demand" value={base_demand_ppm} unit="ppm" testId="result-base-demand" />
        <DataTile label="pH Multiplier" value={`${ph_factor}×`} testId="result-ph-factor" />
        <DataTile label="Temp Multiplier" value={`${temp_factor}×`} testId="result-temp-factor" />
        <DataTile label="Contact Time" value={contact_time_minutes} unit="min" testId="result-contact-time" />
      </div>

      {/* Contact Time card */}
      <SpecCard
        title="Contact Time"
        icon={<Beaker className="w-5 h-5" />}
        testId="result-rt-card"
        rows={[
          ["Tank", retention_tank?.name || "—"],
          ["Volume", retention_tank?.gallons > 0 ? `${retention_tank.gallons} gal` : "—"],
          ["SKU", retention_tank?.sku],
          ["Contact time", `${contact_time_minutes} min`],
          ...(retention_tank?.messages || []).map((m, i) => [`Note ${i + 1}`, m]),
        ]}
      />

      {/* Stock / Tank / Meter */}
      <SpecCard
        title="Stock Chlorine"
        icon={<Beaker className="w-5 h-5" />}
        testId="result-stock-card"
        rows={[
          ["Undiluted stock", `${stock_gallons_per_day} gal/day`],
          ["Mix recommendation", mix_ratio.label],
          ["Diluted solution feed", `${diluted_gallons_per_day} gal/day`],
        ]}
      />
      <SpecCard
        title="Solution Tank"
        icon={<Beaker className="w-5 h-5" />}
        testId="result-tank-card"
        rows={[
          ["Size", `${solution_tank.size_gallons} gallon`],
          ["Refill interval", `~${solution_tank.estimated_days_supply} days`],
          ...(solution_tank.note ? [["Note", solution_tank.note]] : []),
        ]}
      />
      <SpecCard
        title="Inline Water Meter"
        icon={<Activity className="w-5 h-5" />}
        testId="result-meter-card"
        rows={[
          ["SKU", water_meter.sku],
          ["Spec", water_meter.spec],
          ["Wired to", "Stenner PCM"],
        ]}
      />

      {/* Pump detail */}
      <div className="bg-[#111111] border-2 border-white/15 p-5" data-testid="result-pump-detail">
        <div className="flex items-center gap-2 mb-4">
          <Cpu className="w-5 h-5 text-[#F26522]" />
          <div className="font-heading text-base font-black tracking-tight uppercase text-white">Pump Detail</div>
        </div>
        {pump && (
          <div className="space-y-2 font-mono text-sm">
            <Row label="Model" value={pump.model} />
            <Row label="Series" value={pump.series} />
            <Row label="Range" value={`${pump.min_gpd} – ${pump.max_gpd} GPD`} />
            <Row label="Max pressure" value={`${pump.max_psi} PSI`} />
            <Row label="Motor" value={`${pump.rpm} RPM`} />
            <Row label="Setting" value={`${pump_setting_pct}%`} bold />
            <div className="pt-3 border-t border-white/15 text-xs text-white/60 mt-3">{pump.notes}</div>
          </div>
        )}
      </div>

      {/* Pulse mode — SUPER CLEAR: two hero tiles for the seconds and the % the operator sets on the Stenner */}
      <div className="bg-[#111111] border-2 border-[#F26522] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-5 h-5 text-[#F26522]" />
          <div className="font-heading text-lg font-black tracking-tight uppercase text-white">Proportional Pulse Mode</div>
        </div>
        <div className="font-mono text-xs text-white/60 mb-4 leading-relaxed">
          Stenner adjustable pumps (e.g. 45MHP10, 45MHP22) have TWO operator controls.
          Set BOTH on the pump dial / PCM to deliver the dose:
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="bg-[#F26522] text-black p-5" data-testid="pulse-seconds-tile">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-black/70 mb-2">① Run Time Per Pulse</div>
            <div className="font-heading text-5xl font-black tracking-tight leading-none">{pulse_mode.pump_on_seconds_per_pulse}</div>
            <div className="font-mono text-xs tracking-wide uppercase text-black/70 mt-2">seconds (per 1 gal water)</div>
          </div>
          <div className="bg-black text-white border-2 border-[#F26522] p-5" data-testid="pulse-percent-tile">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#F26522] mb-2">② Stroke % Setting</div>
            <div className="font-heading text-5xl font-black tracking-tight leading-none">{pulse_mode.pcm_setting_percent}%</div>
            <div className="font-mono text-xs tracking-wide uppercase text-white/60 mt-2">on the pump dial</div>
          </div>
        </div>

        <div className="space-y-2 font-mono text-sm">
          <Row label="Inline meter" value={`${pulse_mode.meter_pulses_per_gallon} PPG`} />
          <Row label="Solution per gallon water" value={`${pulse_mode.ml_of_solution_per_gallon_water} mL`} />
        </div>
        <div className="pt-3 border-t border-white/15 text-xs text-white/60 mt-3">
          Wire the Stenner PCM (Proportional Control Module) to the 1 PPG inline meter.
          The PCM triggers the pump for the run-time above each time 1 gallon of water passes the meter.
          The stroke % controls how much solution the pump strokes per pulse.
        </div>
      </div>

      {/* Soda Ash co-injection */}
      {soda_ash && (
        <div className="bg-black border-2 border-[#F26522] p-5" data-testid="soda-ash-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#F26522] flex items-center justify-center">
              <FlaskConical className="w-5 h-5 text-black" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading text-xl font-black uppercase tracking-tight text-white">Soda Ash Co-Injection</div>
              <div className="font-mono text-[10px] tracking-wider uppercase text-[#F26522]">
                {soda_ash.auto_triggered ? "Auto-Recommended (Mn + Low pH)" : "Operator-Enabled"}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <DataTile label="pH Δ" value={`${soda_ash.current_ph} → ${soda_ash.target_ph}`} />
            <DataTile label="Dose" value={soda_ash.soda_ash_dose_ppm} unit="ppm" />
            <DataTile label="Stock feed" value={soda_ash.stock_gallons_per_day} unit="gal/day" />
            <DataTile label="Powder/mo" value={soda_ash.lbs_powder_per_month} unit="lb" />
          </div>

          <div className="space-y-2 font-mono text-sm">
            <Row label="Recommended pump" value={soda_ash.pump ? `${soda_ash.pump.model} @ ${soda_ash.pump_setting_pct}%` : "—"} bold />
            <Row label="Mix ratio" value={soda_ash.mix_ratio?.label} />
            <Row label="Solution tank" value={`${soda_ash.solution_tank?.size_gallons} gal`} />
          </div>

          <div className="bg-[#1A1A1A] border-l-4 border-[#F26522] p-3 mt-4 text-xs text-white/80">
            <strong className="text-[#F26522] uppercase tracking-wider">Recipe:</strong> {soda_ash.stock_recipe}
          </div>
          <div className="bg-[#1A1A1A] border-l-4 border-[#F26522] p-3 mt-2 text-xs text-white/80">
            <strong className="text-[#F26522] uppercase tracking-wider">Plumbing:</strong> {soda_ash.injection_point}
          </div>
        </div>
      )}

      {/* Demand breakdown */}
      <div className="bg-[#111111] border-2 border-white/15 p-5">
        <div className="font-heading text-base font-black tracking-tight uppercase mb-4 text-white">Demand Breakdown (ppm Cl₂)</div>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(breakdown).map(([k, v]) => (
            <div key={k} className="bg-black border border-white/10 p-3" data-testid={`breakdown-${k}`}>
              <div className="font-mono text-[10px] tracking-wide uppercase text-white/50">{k.replace(/_/g, " ").replace(" ppm", "")}</div>
              <div className="font-mono text-xl font-medium text-white mt-1">{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <div data-testid="warnings-section">
          <div className="font-heading text-lg font-black tracking-tight uppercase mb-3 flex items-center gap-2 text-[#F26522]">
            <AlertTriangle className="w-5 h-5" /> Field Warnings
          </div>
          <div className="space-y-2">
            {warnings.map((w, i) => (
              <div key={`${i}-${w}`} className="bg-[#111111] border-l-4 border-[#F26522] p-4 text-sm text-white" data-testid={`warning-${i}`}>
                {w}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shock protocol intentionally removed — biofilm flag now shows a flush-the-plumbing warning instead. */}
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#F26522]">{label}</div>
      <div className="text-base font-bold mt-1 truncate text-white">{value}</div>
    </div>
  );
}

function HeroTile({ icon, label, value, unit, orange, black, testId }) {
  const cls = orange
    ? "bg-[#F26522] text-black"
    : black
    ? "bg-black text-white border-2 border-[#F26522]"
    : "bg-[#111111] text-white border-2 border-white/15";
  const iconCls = orange ? "text-black/70" : "text-[#F26522]";
  return (
    <div className={`${cls} p-6`} data-testid={testId}>
      <div className={`flex items-center gap-2 mb-3 ${iconCls}`}>
        {icon}
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase">{label}</div>
      </div>
      <div className="font-heading text-5xl font-black uppercase tracking-tight leading-none">{value}</div>
      {unit && <div className={`font-mono text-xs tracking-wide uppercase mt-2 ${orange ? "text-black/70" : "text-white/50"}`}>{unit}</div>}
    </div>
  );
}

function DataTile({ label, value, unit, testId }) {
  return (
    <div className="bg-[#111111] border-2 border-white/15 p-4" data-testid={testId}>
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50">{label}</div>
      <div className="font-mono text-2xl font-medium text-white mt-2 break-words">
        {value}{unit && <span className="text-sm font-mono text-white/50 ml-1">{unit}</span>}
      </div>
    </div>
  );
}

function SpecCard({ title, icon, rows, testId }) {
  return (
    <div className="bg-[#111111] border-2 border-white/15 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4 text-[#F26522]">
        {icon}
        <div className="font-heading text-base font-black tracking-tight uppercase text-white">{title}</div>
      </div>
      <div className="space-y-3">
        {rows.map(([k, v], i) => (
          <div key={`${k}-${i}`} className="text-sm">
            <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/50">{k}</div>
            <div className="font-mono text-white font-medium mt-0.5 break-words">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-dotted border-white/10 last:border-0">
      <span className="text-white/50 uppercase text-[10px] tracking-wider">{label}</span>
      <span className={`text-white text-right ${bold ? "font-bold text-base" : ""}`}>{value}</span>
    </div>
  );
}

function ShockStat({ label, value }) {
  return (
    <div className="border-2 border-black/30 p-3">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-70">{label}</div>
      <div className="font-mono text-xl font-medium mt-1">{value}</div>
    </div>
  );
}
